<template>
  <v-dialog
      persistent
      v-model="shown"
  >
    <v-row class="mx-0">
      <v-col md="12" class="pa-0">
        <v-card class="elevation-4">
          <v-toolbar dark :color="type === 'leased' ? $parent.dark(asset.toLowerCase()) : $parent.light(asset.toLowerCase())" elevation="2" dense>
            <v-toolbar-title class="ml-4">{{ $parent.capitalize(type) }} {{ asset }}: {{ total }} </v-toolbar-title>
            <v-spacer></v-spacer>
            <v-btn @click="toggleShown()" icon class=""><v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar>
          <v-row class="px-6 pt-3 pb-0 mb-n2">
            <v-text-field dense v-model="searchPending" append-icon="mdi-magnify" label="Search Leases" class="ml-4 mr-1 pt-4 pt-sm-0"></v-text-field>
            <v-btn class="ml-4 mt-1 mr-0" small
                   v-if="type === 'leased'"
                   @click="showLeaseModify = true"
            >Modify</v-btn>
            <v-btn class="ml-4 mt-1 mr-0" small @click="showLeaseReturn = true">Return</v-btn>
            <v-btn class="ml-3 mt-1 mr-2" small v-if="type === 'pending'" @click="showLeaseApprove = true">Approve</v-btn>
          </v-row>

          <v-data-table
              class="row-pointer px-3"
              fixed-header
              :headers="headers"
              height="60vh"
              hide-default-footer
              :items-per-page="-1"
              :items="indexedDetails"
              item-key="journal_num"
              multi-sort
              :search="searchPending"
              show-select
              :single-select="false"
              :sort-desc="true"
              :sort-by="type === 'pending' ? 'creationStamp' : 'inceptStamp'"
              style="white-space: pre"
              v-model="selectedLeases"
          >
            <template v-slot:item.amount="{ item }">
              {{ item.amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',') }}
            </template>
            <template v-slot:item.creationStamp="{ item }">
              {{ item.creationDate }}
            </template>
            <template v-slot:item.inceptStamp="{ item }">
              {{ item.inceptDate }}
            </template>
            <template v-slot:item.expiryStamp="{ item }">
              {{ item.expiryDate }}
            </template>
            <template v-slot:item.nextPaymentStamp="{ item }">
              {{ item.nextPaymentDate }}
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <!--Lease Action Dialogs-->
    <v-row justify="center">
      <ReturnLeasesDialog
          :visible="showLeaseReturn"
          :selected-leases="selectedLeases"
          :onClose="handleReturnDialogClose"
          :resetForm="handleReset"
      />
      <ApproveLeasesDialog
          :visible="showLeaseApprove"
          :selected-leases="selectedLeases"
          :onClose="handleApproveDialogClose"
          :resetForm="handleReset"
          :assetTypeId="assetTypeId"
      />
      <ModifyLeasesDialog
          :visible="showLeaseModify"
          :selected-leases="selectedLeases"
          :onClose="handleModifyDialogClose"
          :resetForm="handleReset"
          :assetTypeId="assetTypeId"
      />
    </v-row>
  </v-dialog>
</template>

<script>
// @ is an alias to /src
import ReturnLeasesDialog from "@/components/reports/leases/ReturnLeasesDialog.vue";
import ApproveLeasesDialog from "@/components/reports/leases/ApproveLeasesDialog.vue";
import ModifyLeasesDialog from "@/components/reports/leases/ModifyLeasesDialog.vue";

export default {
  name: "LeasesDetail",
  components: {
    ModifyLeasesDialog,
    ApproveLeasesDialog,
    ReturnLeasesDialog,
  },
  props: ['asset', 'assetTypeId', 'details', 'index', 'type', 'total'],
  data() {
    return {
      shown: false,

      refresh: false,
      showLeaseReturn: false,
      showLeaseApprove: false,
      showLeaseModify: false,

      hiddenHeaders: [// mobile view doesn't use align, so only add hidden headers on larger screens
        { value: 'creationDate',text: 'hidden',sortable: true, width: '120px',align:' d-none'},// 'd-none' hides column but keeps search ability
        { value: 'inceptDate',  text: 'hidden',sortable: true, width: '120px',align:' d-none'},// 'd-none' hides column but keeps search ability
        { value: 'expiryDate',  text: 'hidden',sortable: true, width: '120px',align:' d-none'},// 'd-none' hides column but keeps search ability
        { value: 'nextPaymentStamp',  text: 'hidden',sortable: true, width: '120px',align:' d-none'},// 'd-none' hides column but keeps search ability
      ],
      leasedTableHeaders: [
        {text:'Begin',      value: 'inceptStamp', align: 'start'},
        {text:'End',        value: 'expiryStamp', align: 'start'},
        {text:'Next Payment',value: 'nextPaymentStamp', align: 'start'},
        {text:'APR',        value: 'apr',         align: 'start'},
        {text:'Commission', value: 'commission',  align: 'start'},
        {text:'Source',     value: 'source',      align: 'start'},
        {text:'Renew',      value: 'renew',       align: 'start'},
        {text:'Compound',   value: 'compound',    align: 'start'},
      ],
      pendingTableHeaders: [
        {text:'Amount',     value: 'amount',       align: 'end'},
        {text:'Lessor',     value: 'username',     align: 'start'},
        {text:'Offer',      value: 'creationStamp',align: 'start'},
      ],
      searchPending: '',
      selectedLeases: [],
    }
  },

  computed: {
    headers() {
      let hdrs = this.type === 'pending' ? this.pendingTableHeaders : this.pendingTableHeaders.concat(this.leasedTableHeaders);
      return this.$vuetify.breakpoint.name === 'xs' ? hdrs : hdrs.concat(this.hiddenHeaders);
    },

    indexedDetails() {
      if (!this.details){
        return [];
      }
      return this.details.map((item) => ({
        creationStamp: item.creation_timestamp,
        inceptStamp: item.incept_timestamp,
        expiryStamp: item.expiry_timestamp,
        nextPaymentStamp: item.next_payment_timestamp,
        creationDate: item.creation_date,
        inceptDate: item.incept_date,
        expiryDate: item.expiry_date,
        nextPaymentDate: item.next_payment_date,
        commission: item.commissioned_party,
        apr: item.apr_percent,
        amount: item.asset_amount,
        source: item.source,
        username: item.username,
        journal_num: item.journal_num,
        renew: item.auto_renew !== 'false',
        compound: item.compound
      }))
    },
  },

  methods: {
    handleReturnDialogClose(){
      this.showLeaseReturn = false;
    },
    handleApproveDialogClose(){
      this.showLeaseApprove = false;
    },
    handleModifyDialogClose(){
      this.showLeaseModify = false;
    },
    async handleReset(){
      await this.$parent.getLeases();
      let leases = this.$parent.leases[this.type + this.asset + 'Leases']
      if (!leases) {// no leases remaining to be viewed so close dialog
        this.toggleShown();
        return;
      }
      this.$parent.details = leases;
      for (let asset of this.$parent.leases[this.type + 'Totals']) {
        if (this.$parent.name(asset.asset_type_id) === this.$parent.specie[this.index].name) this.$parent.total = asset.asset_amount;
      }
      this.selectedLeases  = [];
    },

    toggleShown: function() {
      this.shown = !this.shown;
      this.selectedLeases = [];
    },
  },
}
</script>

<style scoped>

</style>